/**
 * MonthEndReviewList - the "Reviews" pill in Budget > 2026 Stats.
 *
 * A plain list inside one white card: the month in progress first (greyed out,
 * "Ready Oct 1"), then every finished month with data, newest first. Reviews
 * that haven't been opened get a small "New" tag. Tapping a month opens it.
 */
import { useState } from "react";
import { ChevronRight } from "lucide-react";
import customToast from "../../utils/customToast";
import { usePlan } from "../../utils/plan"; // plan_08
import MonthEndReview from "./MonthEndReview";
import {
  getReviewPalette, getReviewState, hasReviewData, markReviewSeen, pinNoteToMonth,
  monthKeyOf, monthNameOf, shiftMonthKey,
} from "../../utils/monthEndReview";

const SANS = "'DM Sans', sans-serif";
const EYEBROW = {
  pink: '#7B5EA7',
  warm: '#2D5A3D',
  blue: '#4361EE'
};

const MonthEndReviewList = ({
  year,
  transactions = [],
  noSpendDays = {},
  sinkingFunds = [],
  categoryBudgets = {},
  userData,
  saveUserData,
  theme = 'pink',
}) => {
  const [openKey, setOpenKey] = useState(null);
  const plan = usePlan();
  const P = getReviewPalette(theme);
  const state = getReviewState(userData);
  const nowKey = monthKeyOf(new Date());
  const thisYear = new Date().getFullYear();

  // Finished months of the chosen year that have data, newest first.
  const months = [];
  for (let m = 12; m >= 1; m--) {
    const key = `${year}-${String(m).padStart(2, '0')}`;
    if (key >= nowKey) continue;
    if (!hasReviewData(transactions, key)) continue;
    months.push(key);
  }

  const openReview = (key) => {
    setOpenKey(key);
    if (userData && saveUserData) markReviewSeen(userData, saveUserData, key);
  };

  const showCurrent = year === thisYear;
  const rowCount = months.length + (showCurrent ? 1 : 0);

  return (
    <div className="pb-2" style={{ fontFamily: SANS }}>
      {!plan && (
      <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: EYEBROW[theme] || EYEBROW.pink }}>
        <span className="wy-star">✦ </span>Your previous month-end reviews
      </p>
      )}

      {rowCount === 0 ? (
        <div className="bg-white rounded-3xl px-5 py-4 text-[15px] text-gray-500" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          No reviews for {year}.
        </div>
      ) : (
        <div className={"bg-white rounded-3xl px-5 py-1" + (plan ? " wy-plan-reviews" : "")} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          {plan && <p className="m-0" style={{ fontSize: 16.5, fontWeight: 700, letterSpacing: '-0.5px', color: '#26252E', padding: '14px 0 4px' }}>Month-end reviews</p>}
          {showCurrent && (
            <div
              className="flex items-center gap-3 py-3.5"
              style={{ boxSizing: 'border-box', minHeight: 56, borderBottom: months.length ? '1px solid rgba(0,0,0,0.05)' : 0 }}
            >
              <span className="flex-grow text-[16px] text-gray-400">{monthNameOf(nowKey)}</span>
              <span className="text-[14px] text-gray-500 whitespace-nowrap">
                {'⏳'} Ready {monthNameOf(shiftMonthKey(nowKey, 1)).slice(0, 3)} 1
              </span>
            </div>
          )}

          {months.map((key, i) => {
            const isNew = !(state.seen && state.seen[key]);
            const last = i === months.length - 1;
            return (
              <button
                key={key}
                type="button"
                onClick={() => openReview(key)}
                aria-label={`Open ${monthNameOf(key)} review${isNew ? ', new' : ''}`}
                className="w-full flex items-center gap-3 py-3.5 text-left bg-transparent"
                style={{ boxSizing: 'border-box', minHeight: 56, paddingLeft: 0, paddingRight: 0, border: 0, borderBottom: last ? 0 : '1px solid rgba(0,0,0,0.05)', fontFamily: SANS }}
              >
                <span className="flex-grow text-[16px] text-gray-900">{monthNameOf(key)}</span>
                {isNew && (
                  plan ? (
                    <span style={{ fontSize: 12, fontWeight: 600, borderRadius: 999, padding: '3px 10px', background: '#FAE4ED', color: '#C04B7C' }}>New</span>
                  ) : (
                  <span className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-900">
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: P.ui.button }} />
                    New
                  </span>
                  )
                )}
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {openKey && (
        <MonthEndReview
          monthKey={openKey}
          transactions={transactions}
          noSpendDays={noSpendDays}
          sinkingFunds={sinkingFunds}
          categoryBudgets={categoryBudgets}
          theme={theme}
          onClose={() => setOpenKey(null)}
          onPinNote={(text, nextKey) => {
            if (!userData || !saveUserData) return;
            pinNoteToMonth(userData, saveUserData, nextKey, text);
            customToast.success('Pinned!', `Added to ${monthNameOf(nextKey)}’s budget notes`);
          }}
        />
      )}
    </div>
  );
};

export default MonthEndReviewList;
